import React, {useEffect} from 'react';

import {format} from 'date-fns';
import {useGate, useUnit} from 'effector-react';

import {TFunction} from 'i18next';

import {useTranslation} from 'react-i18next';

import {generatePath} from 'react-router-dom';

import {userModel} from '@/entities/user/model';
import {UserRole} from '@/entities/user/types';
import {ROUTES} from '@app/routing';
import {DATE_VIEW_PATTERN} from '@consts/date';
import {INVOICE_COLORS} from '@consts/statuses';
import {InvoicesGate, invoicesModel} from '@entities/invoices/model';
import {InvoiceRawRow, InvoiceType} from '@entities/invoices/types';
import {Control} from '@pages/Invoices/ui/Control';
import {GridFieldConfig} from '@shared/types/grid';

import {Link} from '@shared/ui/Link';
import {StatusWrapper} from '@shared/ui/PageComponents';
import {mapTableColumns, Table} from '@shared/ui/Table2.0';
import {handleNil} from '@utils/handleNil';

const config = (
    t: TFunction,
    type: InvoiceType,
    role: UserRole | null,
): GridFieldConfig<keyof InvoiceRawRow>[] => {
    const translate = (key: string) => t('page_invoices.table.' + key);
    const isIncoming = type === 'INCOMING';
    const isTableSplitted = role === 'DIRECTOR' || role === 'EMPLOYEE';

    const commonContractorColumns: GridFieldConfig<keyof InvoiceRawRow>[] = [
        {
            id: 'sellerTaxNumber',
            header: translate('seller_tin'),
            maxWidth: 200,
        },
        {
            id: 'buyerTaxNumber',
            header: translate('buyer_tin'),
            maxWidth: 200,
        },
    ];
    const splitContractorColumns: GridFieldConfig<keyof InvoiceRawRow>[] = [
        {
            id: isIncoming ? 'sellerTaxNumber' : 'buyerTaxNumber',
            header: translate('contractor_tin'),
            maxWidth: 200,
        },
    ];

    const contractorColumns = isTableSplitted ? splitContractorColumns : commonContractorColumns;

    return [
        {
            id: 'irn',
            header: translate('serial_number'),
            isSortable: true,
            width: 250,
        },
        {
            id: 'invoiceId',
            header: translate('invoice_id'),
            width: 320,
        },
        {
            id: 'invoiceDate',
            header: translate('date'),
            isSortable: true,
            width: 150,
        },
        {
            id: 'parentUuid',
            header: translate('parent_number'),
            width: 320,
        },
        ...contractorColumns,
        {
            id: 'amountWithoutVat',
            header: translate('sum_without_taxes'),
            maxWidth: 150,
            isSortable: true,
        },
        {
            id: 'vatSum',
            header: translate('tax_sum'),
            maxWidth: 150,
            isSortable: true,
        },
        {
            id: 'amountWithVat',
            header: translate('sum'),
            maxWidth: 150,
            isSortable: true,
        },
        {
            id: 'currency',
            header: translate('currency'),
            maxWidth: 100,
        },
        {
            id: 'docType',
            header: translate('doc_type'),
            maxWidth: 120,
        },
        {
            id: 'status',
            header: translate('status'),
            width: 160,
        },
        {
            id: 'acceptStatus',
            header: translate('accept_status'),
            maxWidth: 140,
        },
    ];
};

const dataMapper = (
    raw: InvoiceRawRow[] | undefined,
    t: TFunction,
    role: UserRole | null,
): Record<keyof InvoiceRawRow, any>[] => {
    if (!raw?.length) return [];
    const isTableSplitted = role === 'DIRECTOR' || role === 'EMPLOYEE';
    return raw.map((row: InvoiceRawRow) => ({
        ...row,
        status: (
            <StatusWrapper color={INVOICE_COLORS.STATUS[row.status]}>
                {t('terms.invoice.inner_status.' + row.status)}
            </StatusWrapper>
        ),
        invoiceId: (
            <Link
                to={{
                    pathname: generatePath(ROUTES.INVOICE_DETAILS, {id: row.invoiceId}),
                    ...(!isTableSplitted && {
                        search: row.sellerTaxNumber.toString() ? `?tin=${row.sellerTaxNumber}` : '',
                    }),
                    state: {from: location.pathname},
                }}
            >
                {row.invoiceId}
            </Link>
        ),
        invoiceDate: format(new Date(row.invoiceDate), DATE_VIEW_PATTERN),
        docType: (
            <StatusWrapper color={INVOICE_COLORS.DOCUMENT[row.docType]}>
                {t('terms.invoice.type.' + row.docType)}
            </StatusWrapper>
        ),
        acceptStatus: handleNil(row.acceptStatus, (v) => (
            <StatusWrapper color={INVOICE_COLORS.ACCEPT[row.acceptStatus]}>
                {t('terms.invoice.status.' + v)}
            </StatusWrapper>
        )),
        parentUuid: handleNil(row.parentUuid, (v) => {
            if (!row.parentUuid) return v;
            return (
                <Link
                    to={{
                        pathname: generatePath(ROUTES.INVOICE_DETAILS, {id: v}),
                        ...(!isTableSplitted && {
                            search: row.sellerTaxNumber.toString()
                                ? `?tin=${row.sellerTaxNumber}`
                                : '',
                        }),
                        state: {from: location.pathname},
                    }}
                >
                    {v}
                </Link>
            );
        }),
    }));
};

export const InvoicesList = () => {
    const {t} = useTranslation();
    const invoices = useUnit(invoicesModel);
    const user = useUnit(userModel);
    const path = location.pathname;
    useEffect(() => {
        invoices.typeChanged(path === ROUTES.INVOICES_OUTGOING ? 'OUTGOING' : 'INCOMING');
    }, [location.pathname]);

    useGate(InvoicesGate, {type: invoices.state.type});

    const data = React.useMemo(() => dataMapper(invoices.state.data?.data, t, user.state.role), [
        t,
        invoices.state.data,
        user.state.role,
    ]);
    const columns = React.useMemo(
        () =>
            mapTableColumns(
                config(t, invoices.state.type, user.state.role),
                invoices.state.filters,
                invoices.filtersChanged,
                invoices.filtersReset,
                invoices.state.sortings,
                invoices.sortingsChanged,
            ),
        [t, invoices.state.filters, invoices.state.sortings],
    );

    return user.state.role === 'DIRECTOR' || user.state.role === 'EMPLOYEE' ? (
        <Table
            headerControls={<Control />}
            tableTabsSettings={{
                baseUrl: '/invoices',
                links: [
                    {label: t('page_invoices.in'), path: 'incoming'},
                    {label: t('page_invoices.out'), path: 'outgoing'},
                ],
            }}
            columns={columns}
            data={data}
            isLoading={invoices.state.pending}
            hasNext={Boolean(invoices.state.data?.nextPageRef)}
            pageIndex={invoices.state.pageNumber}
            onPageChange={invoices.pageNumberChanged}
            onPageSizeChange={invoices.pageSizeChanged}
        />
    ) : (
        <Table
            headerControls={<Control />}
            columns={columns}
            data={data}
            isLoading={invoices.state.pending}
            hasNext={Boolean(invoices.state.data?.nextPageRef)}
            pageIndex={invoices.state.pageNumber}
            onPageChange={invoices.pageNumberChanged}
            onPageSizeChange={invoices.pageSizeChanged}
        />
    );
};
